import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { estimatesAPI } from '../../api';
import { stripIframePreviewScripts } from '../../lib/stripIframePreviewScripts';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import {
  labelPortalRejectTags,
  portalFeedbackActionLabel,
  PORTAL_QUERY_REASON_LABELS,
} from '../../lib/estimateClientPortalFeedback';
import { Pencil, Printer, Copy, ArrowLeft, Mail, RefreshCw } from 'lucide-react';

export default function EstimateView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [html, setHtml] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [reissuing, setReissuing] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [htmlDoc, est] = await Promise.all([
        estimatesAPI.getHtml(id, { embed: true }),
        estimatesAPI.getOne(id),
      ]);
      setHtml(stripIframePreviewScripts(typeof htmlDoc === 'string' ? htmlDoc : ''));
      setEstimate(est);
    } catch (e) {
      setError(e.message || 'Failed to load estimate');
      setHtml('');
      setEstimate(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!id || !estimate?.clientPortalResponsePending) return;
    let cancelled = false;
    (async () => {
      try {
        await estimatesAPI.acknowledgeClientResponse(id);
        if (!cancelled) {
          setEstimate((prev) =>
            prev ? { ...prev, clientPortalResponsePending: false } : prev
          );
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, estimate?.clientPortalResponsePending]);

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
    }
  };

  const handleDuplicate = async () => {
    if (!id || duplicating) return;
    try {
      setDuplicating(true);
      setError('');
      const data = await estimatesAPI.duplicate(id);
      const newId = data?.estimate?.id;
      if (!newId) throw new Error('Duplicate failed');
      navigate(`/tenant/estimates/${newId}`);
    } catch (e) {
      setError(e.message || 'Could not duplicate estimate');
      console.error(e);
    } finally {
      setDuplicating(false);
    }
  };

  const handleReissue = async () => {
    if (!id || reissuing) return;
    const ok = window.confirm(
      'Re-issue this estimate as Sent? Any stored client link response on this estimate will be cleared (recent activity is kept).'
    );
    if (!ok) return;
    try {
      setReissuing(true);
      setError('');
      await estimatesAPI.reissue(id);
      await load();
    } catch (e) {
      setError(
        e.response?.data?.error || e.response?.data?.message || e.message || 'Could not re-issue estimate'
      );
    } finally {
      setReissuing(false);
    }
  };

  const deleted = estimate?.isDeleted;
  const canReissue =
    estimate && ['rejected', 'queried', 'accepted'].includes(estimate.status) && !deleted;

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold truncate">
                {estimate?.estimateNum ? `Estimate ${estimate.estimateNum}` : 'Estimate'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Read-only preview (same layout as client print view)
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/estimates/${id}`)}
                disabled={deleted}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/estimates/${id}/send`)}
                disabled={loading || !id || deleted}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send
              </Button>
              {canReissue && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReissue}
                  disabled={reissuing || loading}
                  className="border-sky-600/40 text-sky-800 dark:text-sky-200 bg-[var(--ds-control-field-bg)] disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${reissuing ? 'animate-spin' : ''}`} />
                  {reissuing ? 'Re-issuing…' : 'Re-issue as sent'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={!html || loading}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)]"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDuplicate}
                disabled={duplicating || loading || !estimate || deleted}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <Copy className="h-4 w-4 mr-2" />
                {duplicating ? 'Duplicating…' : 'Duplicate'}
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/tenant/estimates')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {estimate?.clientPortalResponse && (
          <Card className="mb-4 border border-sky-300/80 dark:border-sky-800 bg-sky-50/80 dark:bg-sky-950/30">
            <CardContent className="p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-[var(--ds-text-primary)]">Client link feedback</h3>
                <Badge variant="warning" className="text-[10px]">
                  {portalFeedbackActionLabel(estimate.clientPortalResponse.action)}
                </Badge>
              </div>
              {estimate.clientPortalResponse.action === 'rejected' && (
                <div className="text-xs text-[var(--ds-text-secondary)] space-y-1">
                  {labelPortalRejectTags(estimate.clientPortalResponse.reasonTags).length > 0 && (
                    <p>
                      <span className="font-medium text-[var(--ds-text-primary)]">Reasons: </span>
                      {labelPortalRejectTags(estimate.clientPortalResponse.reasonTags).join(', ')}
                    </p>
                  )}
                  {estimate.clientPortalResponse.comment ? (
                    <p>
                      <span className="font-medium text-[var(--ds-text-primary)]">Comment: </span>
                      {estimate.clientPortalResponse.comment}
                    </p>
                  ) : null}
                </div>
              )}
              {estimate.clientPortalResponse.action === 'queried' && (
                <div className="text-xs text-[var(--ds-text-secondary)] space-y-1">
                  <p>
                    <span className="font-medium text-[var(--ds-text-primary)]">Reason: </span>
                    {PORTAL_QUERY_REASON_LABELS[estimate.clientPortalResponse.queryReason] ||
                      estimate.clientPortalResponse.queryReason}
                  </p>
                  {estimate.clientPortalResponse.details ? (
                    <p>
                      <span className="font-medium text-[var(--ds-text-primary)]">Details: </span>
                      {estimate.clientPortalResponse.details}
                    </p>
                  ) : null}
                </div>
              )}
              {estimate.clientPortalResponse.action === 'accepted' && (
                <p className="text-xs text-[var(--ds-text-secondary)]">
                  The client accepted this estimate via the public link
                  {estimate.clientPortalResponse.at
                    ? ` (${new Date(estimate.clientPortalResponse.at).toLocaleString('en-GB')})`
                    : ''}
                  .
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="overflow-hidden bg-[var(--ds-surface-panel)] border-[var(--ds-border-subtle)]">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading preview…</p>
              </div>
            ) : html ? (
              <iframe
                ref={iframeRef}
                title={`Estimate ${estimate?.estimateNum || ''}`}
                srcDoc={html}
                sandbox="allow-same-origin allow-modals"
                className="w-full min-h-[85vh] border-0 bg-white block"
              />
            ) : (
              <p className="p-8 text-sm text-[var(--ds-text-muted)] text-center">No preview available.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
